import React, { useRef, useState, useEffect } from 'react'
import { Formik, FieldArray } from 'formik'
import { Form as RBForm } from 'react-bootstrap'
import classNames from 'classnames'
import Imgix from 'react-imgix'
import { useParams } from 'react-router-dom'
import {
  useResolutionService,
  useNotificationService,
} from '@abroad/components'
import API from '../../utils/API'
import axios from 'axios'
import ImageCropModal from '../common/ImageCropModal'
import {
  queueAutosaveOnBlur,
  runQueuedFormikSave,
  FormikDirtyReporter,
} from './questAutosave'
import ExperienceConfirmModal from './ExperienceConfirmModal'

const defaultSliderImage = { fileName: null, altText: '' }

const buildSliderPayload = (values, questId) => {
  const validImages = values.headerImage.filter(
    (img) => img && (img.fileName || img.altText),
  )
  return {
    headerImage: validImages.map((img) => ({
      ...img,
      altText: typeof img.altText === 'string' ? img.altText : '',
    })),
    id: questId,
  }
}

const QuestSliderImages = ({ questDetail, addUpdateQuest }) => {
  const [initialImages, setInitialImages] = useState(
    Array.isArray(questDetail.headerImage) &&
      questDetail.headerImage?.length > 0
      ? questDetail.headerImage
      : [],
  )
  const [uploading, setUploading] = useState(false)
  const fileInputRef = useRef(null)
  const [selectedIndex, setSelectedIndex] = useState(null)
  const [draggedIndex, setDraggedIndex] = useState(null)
  const [dragOverIndex, setDragOverIndex] = useState(null)
  const [uploadCancelled, setUploadCancelled] = useState(false)
  const [showImageCropModal, setShowImageCropModal] = useState(false)
  const [selectedImageFile, setSelectedImageFile] = useState(null)
  const [pendingRemoveIndex, setPendingRemoveIndex] = useState(null)
  const inputResetRef = useRef(null)
  const valuesRef = useRef({ headerImage: initialImages })
  const { questId } = useParams()
  const { isMDScreen } = useResolutionService()
  const Notification = useNotificationService()

  useEffect(() => {
    const next =
      Array.isArray(questDetail.headerImage) &&
      questDetail.headerImage?.length > 0
        ? questDetail.headerImage
        : []
    setInitialImages(next)
    valuesRef.current = { headerImage: next }
  }, [questDetail.headerImage])

  const handleAddImageClick = (idx) => {
    if (uploading) {
      return
    }
    setSelectedIndex(idx)
    fileInputRef.current.click()
  }

  const validateImageFile = (imageFile) => {
    const allowedFormats = ['JPG', 'JPEG', 'PNG']
    const fileExtension = imageFile?.name?.split('.').pop()?.toUpperCase()
    if (!allowedFormats.includes(fileExtension)) {
      Notification.showNotification(
        'Filetype must be one of [JPG, JPEG, PNG]',
        'danger',
      )
      return false
    }

    if (
      imageFile.type === 'image/webp' ||
      imageFile.name.toLowerCase().endsWith('.webp')
    ) {
      Notification.showNotification(
        'WebP format is not supported. Please use JPG, JPEG, or PNG format.',
        'danger',
      )
      return false
    }
    return true
  }

  const openCropModal = (imageFile, resetInput) => {
    if (!imageFile) return
    if (!validateImageFile(imageFile)) {
      if (resetInput) resetInput()
      return
    }
    inputResetRef.current = resetInput || null
    setSelectedImageFile(imageFile)
    setShowImageCropModal(true)
  }

  const resetImageInput = () => {
    if (typeof inputResetRef.current === 'function') {
      inputResetRef.current()
    }
    inputResetRef.current = null
  }

  const handleCropModalClose = () => {
    setShowImageCropModal(false)
    setSelectedImageFile(null)
    resetImageInput()
  }

  const uploadSliderImage = async (imageFile) => {
    try {
      const contentType = imageFile.name
        .substr(imageFile.name.lastIndexOf('.') + 1)
        .toUpperCase()
      const { data } = await API.AdminAPI.quests.getQuestImage(
        questId,
        contentType,
      )
      if (data) {
        if (uploadCancelled) {
          setUploadCancelled(false)
          throw new Error('Upload cancelled')
        }

        await axios.put(data.url.url, imageFile, {
          headers: {
            'Content-Type': data.url.contentType,
          },
        })

        if (uploadCancelled) {
          setUploadCancelled(false)
          throw new Error('Upload cancelled')
        }

        setUploading(false)
        return data.fileName
      } else {
        throw new Error('Image upload failed')
      }
    } catch (e) {
      throw e
    } finally {
      setTimeout(() => {
        setUploading(false)
        setUploadCancelled(false)
      }, 850)
    }
  }

  const handleDragStart = (e, index) => {
    if (uploading) {
      e.preventDefault()
      return
    }
    setDraggedIndex(index)
    e.dataTransfer.effectAllowed = 'move'
    e.dataTransfer.setData('text/html', e.target.parentNode)
    e.dataTransfer.setDragImage(e.target, 0, 0)
  }

  const handleDragOver = (e, index) => {
    e.preventDefault()
    if (uploading) {
      return
    }
    e.dataTransfer.dropEffect = 'move'
    setDragOverIndex(index)
  }

  const handleDragLeave = () => {
    setDragOverIndex(null)
  }

  const handleDrop = (e, dropIndex, setFieldValue, values) => {
    e.preventDefault()
    if (uploading) {
      setDragOverIndex(null)
      return
    }
    const files = e.dataTransfer?.files

    if (files && files.length > 0) {
      setSelectedIndex(dropIndex)
      setDragOverIndex(null)
      openCropModal(files[0])
      e.dataTransfer.clearData()
      return
    }

    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null)
      setDragOverIndex(null)
      return
    }

    const newImages = [...values.headerImage]
    const draggedItem = newImages[draggedIndex]
    newImages.splice(draggedIndex, 1)
    newImages.splice(dropIndex, 0, draggedItem)

    setFieldValue('headerImage', newImages)
    setDraggedIndex(null)
    setDragOverIndex(null)
  }

  const handleDragEnd = () => {
    setDraggedIndex(null)
    setDragOverIndex(null)
  }

  return (
    <Formik
      initialValues={{ headerImage: initialImages }}
      enableReinitialize
      onSubmit={async (values, { resetForm }) => {
        const validImages = values.headerImage.filter(
          (img) => img && (img.fileName || img.altText),
        )

        if (values.headerImage.length === 1 && validImages.length === 0) {
          return
        }

        await runQueuedFormikSave({
          values,
          valuesRef,
          buildPayload: (v) => buildSliderPayload(v, questId),
          resetForm,
          setInitialValues: (latest) => {
            setInitialImages(latest.headerImage)
            valuesRef.current = latest
          },
          save: (payload) => addUpdateQuest(payload, { silent: true }),
        })
      }}>
      {(formik) => {
        const { values, setFieldValue, handleSubmit, dirty } = formik
        valuesRef.current = formik.values
        const fieldBlur = () => queueAutosaveOnBlur(formik)
        const persistImages = (nextImages) => {
          const validImages = (nextImages || []).filter(
            (img) => img && (img.fileName || img.altText),
          )
          if (nextImages?.length === 1 && validImages.length === 0) {
            addUpdateQuest({ headerImage: [], id: questId }, { silent: true })
            return
          }
          addUpdateQuest(
            {
              headerImage: validImages.map((img) => ({
                ...img,
                altText: typeof img.altText === 'string' ? img.altText : '',
              })),
              id: questId,
            },
            { silent: true },
          )
        }

        return (
          <form onSubmit={handleSubmit} aria-busy={uploading || undefined}>
            <FormikDirtyReporter id='content-hero' dirty={dirty} />
            <p className='experience-f-hint-top text-dark'>
              These rotate across the top of the Prosperity page.
            </p>
            <FieldArray name='headerImage'>
              {({ push, remove, move }) => (
                <>
                  <div className='experience-img-grid'>
                    {values.headerImage.map((img, idx) => {
                      const hasImage = !!img.fileName
                      const slotUploading = uploading && selectedIndex === idx
                      return (
                        <div
                          key={idx}
                          className={classNames('experience-img-slot', {
                            'is-filled': hasImage,
                          })}
                          data-ratio='1024:465'
                          style={{
                            opacity: draggedIndex === idx ? 0.5 : 1,
                            outline:
                              dragOverIndex === idx
                                ? '2px dashed var(--gold-deep, #bf9000)'
                                : undefined,
                          }}
                          draggable={hasImage && !uploading}
                          onDragStart={(e) =>
                            hasImage && handleDragStart(e, idx)
                          }
                          onDragOver={(e) => handleDragOver(e, idx)}
                          onDragLeave={handleDragLeave}
                          onDrop={(e) => {
                            handleDrop(e, idx, setFieldValue, values)
                            window.setTimeout(() => {
                              queueAutosaveOnBlur(formik)
                            }, 0)
                          }}
                          onDragEnd={handleDragEnd}>
                          <div
                            className={classNames('experience-img-frame', {
                              'is-empty': !hasImage && !slotUploading,
                            })}
                            onClick={() => {
                              if (!uploading && draggedIndex === null) {
                                handleAddImageClick(idx)
                              }
                            }}
                            role='button'
                            tabIndex={uploading ? -1 : 0}
                            aria-disabled={uploading || undefined}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault()
                                if (!uploading && draggedIndex === null) {
                                  handleAddImageClick(idx)
                                }
                              }
                            }}>
                            {idx === 0 && hasImage ? (
                              <span className='experience-img-badge'>
                                Loads first
                              </span>
                            ) : null}

                            {slotUploading ? (
                              <span
                                className='experience-img-hover'
                                style={{ opacity: 1 }}>
                                Uploading…
                              </span>
                            ) : (
                              <span className='experience-img-hover'>
                                {hasImage ? 'Click to replace' : 'Upload image'}
                              </span>
                            )}

                            {hasImage ? (
                              img.fileName.startsWith('data:') ? (
                                <img
                                  src={img.fileName}
                                  alt={img.altText || `Slider ${idx + 1}`}
                                />
                              ) : (
                                <Imgix
                                  imgixParams={{
                                    fit: 'crop',
                                    crop: 'center',
                                    ar: isMDScreen ? '85:186' : '1024:465',
                                  }}
                                  src={`${process.env.REACT_APP_IMG_SOURCE}/quest/${img.fileName}`}
                                  width={'100%'}
                                  height={'100%'}
                                  alt={img.altText || `Slider ${idx + 1}`}
                                  className='lazyload'
                                />
                              )
                            ) : !slotUploading ? (
                              <>
                                <b>Upload image</b>
                                <small>
                                  JPG, JPEG or PNG · cropped to 1024:465
                                </small>
                              </>
                            ) : null}

                            {(hasImage || img.altText) && (
                              <button
                                type='button'
                                className='experience-img-remove'
                                aria-label='Remove image'
                                disabled={uploading && selectedIndex !== idx}
                                onClick={(e) => {
                                  e.stopPropagation()
                                  if (slotUploading) {
                                    setUploadCancelled(true)
                                    setUploading(false)
                                    const next = values.headerImage.filter(
                                      (_, i) => i !== idx,
                                    )
                                    remove(idx)
                                    window.setTimeout(
                                      () => persistImages(next),
                                      0,
                                    )
                                    return
                                  }
                                  setPendingRemoveIndex(idx)
                                }}>
                                ×
                              </button>
                            )}
                          </div>
                          <RBForm.Control
                            name={`headerImage[${idx}].altText`}
                            placeholder='One-line explanation of this image'
                            value={img.altText || ''}
                            onChange={(e) =>
                              setFieldValue(
                                `headerImage[${idx}].altText`,
                                e.target.value,
                              )
                            }
                            onBlur={fieldBlur}
                            className='experience-f-control experience-img-alt'
                          />
                        </div>
                      )
                    })}
                  </div>
                  <div className='experience-form-actions'>
                    <button
                      type='button'
                      className='experience-btn-sm btn-outline-experience'
                      disabled={uploading}
                      onClick={() => {
                        if (!uploading) {
                          push({ ...defaultSliderImage })
                          setSelectedIndex(values.headerImage?.length)
                          setTimeout(() => fileInputRef.current.click(), 0)
                        }
                      }}>
                      Add New Image
                    </button>
                  </div>
                </>
              )}
            </FieldArray>
            <RBForm.Control
              type='file'
              accept='image/jpg, image/jpeg, image/png'
              style={{ display: 'none' }}
              onChange={async (e) => {
                const target = e.target
                const files = e.target.files
                if (files && files?.length > 0) {
                  openCropModal(files[0], () => {
                    if (target) target.value = ''
                  })
                }
              }}
              name='fileName'
              ref={fileInputRef}
            />
            <ImageCropModal
              show={showImageCropModal}
              onHide={handleCropModalClose}
              file={selectedImageFile}
              onConfirm={async (croppedImageFile) => {
                if (!croppedImageFile || selectedIndex === null) return
                setShowImageCropModal(false)
                setUploading(true)
                const reader = new FileReader()
                reader.onloadend = async () => {
                  setFieldValue(
                    `headerImage[${selectedIndex}].fileName`,
                    reader.result,
                  )
                  try {
                    const fileName = await uploadSliderImage(croppedImageFile)
                    setFieldValue(
                      `headerImage[${selectedIndex}].fileName`,
                      fileName,
                    )
                    const next = values.headerImage.map((img, i) =>
                      i === selectedIndex ? { ...img, fileName } : img,
                    )
                    persistImages(next)
                  } catch (e) {
                    if (e.message !== 'Upload cancelled') {
                      Notification.showNotification(
                        'Image upload failed',
                        'danger',
                      )
                    }
                  } finally {
                    setUploading(false)
                    setSelectedImageFile(null)
                    resetImageInput()
                  }
                }
                reader.readAsDataURL(croppedImageFile)
              }}
              isUploading={uploading}
              aspect={isMDScreen ? 85 / 186 : 1024 / 465}
              ratioLabel={isMDScreen ? '85:186' : '1024:465'}
              title='Crop Image'
            />
            <ExperienceConfirmModal
              show={pendingRemoveIndex != null}
              onHide={() => setPendingRemoveIndex(null)}
              onConfirm={() => {
                const idx = pendingRemoveIndex
                setPendingRemoveIndex(null)
                if (idx == null) return
                const next = values.headerImage.filter((_, i) => i !== idx)
                setFieldValue('headerImage', next, false)
                window.setTimeout(() => persistImages(next), 0)
              }}
              title='Remove this image?'
              message='The image will be cleared. You can upload another.'
              confirmLabel='Delete'
            />
          </form>
        )
      }}
    </Formik>
  )
}

export default QuestSliderImages
